/**
 * SeriesSectionRenderers - React components for series Page Builder templates
 * Location: /app/frontend/src/components/page-builder/series/
 * 
 * These components render the series detail templates within the Page Builder framework.
 * They fetch their own data based on the seriesId from URL params or context.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../App';
import { apiClient } from '../../../config/api';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Heart,
  Share2,
  DollarSign,
  Eye,
  Users,
  Star,
  BookOpen,
  Lock,
  Clock,
  ChevronRight,
  MessageCircle,
  Flame,
  Laugh,
  Zap,
  AlertTriangle,
  Loader2,
  UserPlus,
  Trash2,
} from 'lucide-react';

// Use apiClient which has the correct baseURL and interceptors

// ============================================================================
// SHARED HOOKS & UTILITIES
// ============================================================================

/**
 * Hook to fetch series data - shared across section renderers
 */
export const useSeriesData = (seriesId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await apiClient.get(`/series/${seriesId}/detail`);
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('[useSeriesData] Error:', err);
        if (err.response?.status === 404) {
          setError('Series not found');
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [seriesId]);

  return { data, loading, error, refetch: () => setLoading(true) };
};

/**
 * Hook to fetch episodes
 */
export const useEpisodes = (seriesId) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    const fetchEpisodes = async () => {
      try {
        const response = await apiClient.get(`/series/${seriesId}/chapters`);
        setEpisodes(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useEpisodes] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodes();
  }, [seriesId]);

  return { episodes, setEpisodes, loading, error };
};

/**
 * Hook to fetch comments
 */
export const useComments = (seriesId, limit = 10) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    const fetchComments = async () => {
      try {
        const response = await apiClient.get(`/series/${seriesId}/comments?limit=${limit}`);
        setComments(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useComments] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [seriesId, limit]);

  return { comments, setComments, loading, error };
};

/**
 * Hook to fetch related series
 */
export const useRelatedSeries = (seriesId, limit = 6) => {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    const fetchRelated = async () => {
      try {
        const response = await apiClient.get(`/series/${seriesId}/related?limit=${limit}`);
        setRelated(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useRelatedSeries] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelated();
  }, [seriesId, limit]);

  return { related, loading, error };
};

/**
 * Section loading skeleton
 */
const SectionLoading = ({ className = '' }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="h-8 bg-zinc-800 rounded w-1/3 mb-4" />
    <div className="space-y-3">
      <div className="h-4 bg-zinc-800 rounded w-full" />
      <div className="h-4 bg-zinc-800 rounded w-5/6" />
      <div className="h-4 bg-zinc-800 rounded w-4/6" />
    </div>
  </div>
);

/**
 * Section error state
 */
const SectionError = ({ message, onRetry }) => (
  <div className="text-center py-8 bg-red-500/10 border border-red-500/20 rounded-lg">
    <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
    <p className="text-red-400 text-sm mb-3">{message || 'Failed to load content'}</p>
    {onRetry && (
      <Button onClick={onRetry} variant="outline" size="sm" className="text-red-400 border-red-500/50">
        Retry
      </Button>
    )}
  </div>
);

// ============================================================================
// SERIES HERO SECTION
// ============================================================================

/**
 * SeriesHeroSection - Dynamic hero with cover, title, creator info
 */
export const SeriesHeroSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const { data, loading, error } = useSeriesData(seriesId);
  const [subscribed, setSubscribed] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);

  const props = block?.props || {};

  // Check subscription status
  useEffect(() => {
    if (!user || !seriesId) return;
    
    const checkSubscription = async () => {
      try {
        const res = await apiClient.get(`/series/${seriesId}/subscription-status`);
        setSubscribed(res.data.subscribed);
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };
    
    checkSubscription();
  }, [user, seriesId]);

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/auth');
      return;
    }

    try {
      if (subscribed) {
        await apiClient.delete(`/series/${seriesId}/subscribe`);
        setSubscribed(false);
        toast.success('Unsubscribed successfully');
      } else {
        await apiClient.post(`/series/${seriesId}/subscribe`);
        setSubscribed(true);
        toast.success('Subscribed successfully! 🎉');
      }
    } catch (err) {
      console.error('Error toggling subscription:', err);
      toast.error('Failed to update subscription');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: data?.series?.title,
        text: data?.series?.description,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <section className="relative min-h-[400px] bg-zinc-900" data-testid="series-hero-loading">
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-64 h-96 bg-zinc-800 rounded-lg animate-pulse" />
            <div className="flex-1 space-y-4">
              <div className="h-12 bg-zinc-800 rounded w-2/3 animate-pulse" />
              <div className="h-6 bg-zinc-800 rounded w-1/3 animate-pulse" />
              <div className="h-8 bg-zinc-800 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="relative min-h-[300px] bg-zinc-900 flex items-center justify-center">
        <SectionError message="Failed to load series data" />
      </section>
    );
  }

  const { series, creator, stats } = data;

  return (
    <section 
      className="relative" 
      data-testid="series-hero-section"
      data-series-id={seriesId}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: series.billboard_url
            ? `url('${series.billboard_url}')`
            : series.cover_url
              ? `url('${series.cover_url}')`
              : 'linear-gradient(to bottom right, #18181b, #000000)',
          filter: series.billboard_url ? 'none' : 'blur(20px)',
          transform: 'scale(1.1)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />

      <div className="relative container mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Cover Image */}
          {props.showCover !== false && (
            <div className="flex-shrink-0">
              <div className="w-64 h-96 bg-black/60 rounded-lg shadow-2xl border-2 border-orange-500 flex items-center justify-center overflow-hidden">
                {series.cover_url ? (
                  <img
                    src={series.cover_url}
                    alt={series.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <BookOpen className="w-16 h-16 text-zinc-600" />
                )}
              </div>
            </div>
          )}

          {/* Series Info */}
          <div className="flex-1">
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">{series.title}</h1>

            {/* Tags */}
            {props.showTags !== false && (
              <div className="flex flex-wrap gap-2 mb-4">
                {series.genres?.map((genre) => (
                  <Badge key={genre} className="bg-orange-500 text-white">
                    {genre}
                  </Badge>
                ))}
                <Badge
                  className={`${
                    series.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                  } text-white`}
                >
                  {series.status === 'completed' ? 'Completed' : 'Ongoing'}
                </Badge>
              </div>
            )}

            {/* Stats */}
            {props.showStats !== false && stats && (
              <div className="flex flex-wrap gap-6 mb-6 text-zinc-300">
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5 text-orange-400" />
                  <span>{stats.views?.toLocaleString() || 0} views</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-orange-400" />
                  <span>{stats.subscribers?.toLocaleString() || 0} subscribers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-orange-400" />
                  <span>{stats.chapters || 0} episodes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span>{stats.rating || 0}/10</span>
                </div>
              </div>
            )}

            {/* Creator Link */}
            {creator?.profile && (
              <Link
                to={creator.profile.handle ? `/creator/${creator.profile.handle}` : `/creator/${creator.profile.id}`}
                className="flex items-center space-x-3 mb-6 p-3 bg-black/50 rounded-lg hover:bg-black/70 transition-colors w-fit"
              >
                <Avatar className="w-12 h-12 border-2 border-orange-500">
                  <AvatarImage src={creator.profile.avatar_url} />
                  <AvatarFallback className="bg-orange-500 text-white">
                    {creator.profile.display_name?.slice(0, 2).toUpperCase() || 'CR'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-white font-medium">{creator.profile.display_name}</p>
                  <p className="text-zinc-400 text-sm">@{creator.profile.handle}</p>
                </div>
              </Link>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {props.showSubscribeButton !== false && (
                <Button
                  onClick={handleSubscribe}
                  className={`${
                    subscribed
                      ? 'bg-zinc-700 hover:bg-zinc-600'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                  } text-white font-bold`}
                  data-testid="subscribe-button"
                >
                  <Heart className={`w-4 h-4 mr-2 ${subscribed ? 'fill-white' : ''}`} />
                  {subscribed ? 'Subscribed' : 'Subscribe'}
                </Button>
              )}

              <Dialog open={showTipModal} onOpenChange={setShowTipModal}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Tip Creator
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Support {creator?.profile?.display_name}</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Show your appreciation with a tip!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                      $2 - Small Tip
                    </Button>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
                      $5 - Medium Tip
                    </Button>
                    <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-bold">
                      $10 - Large Tip
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {props.showShareButton !== false && (
                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// EPISODE LIST SECTION
// ============================================================================

/**
 * EpisodeListSection - Sortable list of episodes/chapters
 */
export const EpisodeListSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const { episodes, setEpisodes, loading, error } = useEpisodes(seriesId);
  const { data: seriesData } = useSeriesData(seriesId);
  const [episodeSort, setEpisodeSort] = useState('newest');
  const [updatingEpisodeId, setUpdatingEpisodeId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const props = block?.props || {};
  const heading = props.heading || 'Episodes';

  // Check if user is owner
  const isOwner = user && seriesData?.owner_user_id === user.id && 
    !(user.role === 'admin' || user.is_admin === true);

  const sortedEpisodes = [...episodes].sort((a, b) => {
    const aNum = Number(a.chapter_number) || 0;
    const bNum = Number(b.chapter_number) || 0;
    return episodeSort === 'newest' ? bNum - aNum : aNum - bNum;
  });

  const handleTogglePublish = async (episode) => {
    if (!user) {
      toast.error('Please login to manage episodes');
      navigate('/auth');
      return;
    }

    const currentlyPublished = episode.is_published === 'published';
    const nextStatus = currentlyPublished ? 'unpublished' : 'published';

    setUpdatingEpisodeId(episode.id);
    try {
      const response = await apiClient.patch(
        `/series/${seriesId}/chapters/${episode.id}/status`,
        { is_published: nextStatus }
      );
      setEpisodes((prev) => prev.map((ep) => 
        ep.id === episode.id ? { ...ep, ...response.data } : ep
      ));
      toast.success(currentlyPublished ? 'Episode unpublished' : 'Episode published');
    } catch (err) {
      console.error('Error updating episode status:', err);
      toast.error(err.response?.data?.detail || 'Failed to update episode status');
    } finally {
      setUpdatingEpisodeId(null);
    }
  };

  const handleDeleteEpisode = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/series/${seriesId}/chapters/${deleteTarget.id}`);
      setEpisodes((prev) => prev.filter((ep) => ep.id !== deleteTarget.id));
      toast.success('Episode deleted');
    } catch (err) {
      console.error('Error deleting episode:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete episode');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <section className="py-8" data-testid="episode-list-loading">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6">
          <SectionError message="Failed to load episodes" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-8" data-testid="episode-list-section">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{heading}</h2>
          {props.allowSort !== false && (
            <Select value={episodeSort} onValueChange={setEpisodeSort}>
              <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Episode List */}
        <div className="space-y-3">
          {sortedEpisodes.map((episode) => {
            const publishStatus = episode.is_published;
            const statusLabel = publishStatus === 'published' ? 'Published' : 
              publishStatus === 'draft' ? 'Draft' : 'Unpublished';
            const statusColor = publishStatus === 'published' ? 'bg-green-600 text-white' :
              publishStatus === 'draft' ? 'bg-yellow-600 text-white' : 'bg-zinc-700 text-zinc-200';

            return (
              <Card
                key={episode.id}
                className="bg-zinc-900 border-zinc-800 hover:border-orange-500 transition-all duration-300 group"
                data-testid={`episode-card-${episode.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    {/* Episode Thumbnail (Webtoons-style) */}
                    {props.showThumbnails !== false && (
                      <Link
                        to={`/series/${seriesId}/read/${episode.id}`}
                        className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg flex-shrink-0 overflow-hidden border border-zinc-700 group-hover:border-orange-500/50 transition-colors"
                        data-testid={`episode-thumb-${episode.id}`}
                      >
                        {episode.cover_url ? (
                          <img
                            src={episode.cover_url}
                            alt={`Episode ${episode.chapter_number} cover`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : seriesData?.series?.cover_url ? (
                          <img
                            src={seriesData.series.cover_url}
                            alt="Series cover"
                            className="w-full h-full object-cover opacity-60"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                            <span className="text-lg font-bold text-orange-500/70">
                              {episode.chapter_number || '#'}
                            </span>
                          </div>
                        )}
                      </Link>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2 min-w-0">
                          <Link
                            to={`/series/${seriesId}/read/${episode.id}`}
                            className="hover:text-orange-400 truncate"
                          >
                            <h3 className="text-white font-bold truncate">
                              Episode {episode.chapter_number}: {episode.title}
                            </h3>
                          </Link>
                          {episode.is_free === 'false' && (
                            <Lock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          )}
                        </div>
                        {props.showStatus !== false && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                            {statusLabel}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-sm text-zinc-400">
                        <div className="flex items-center space-x-4">
                          {props.showPublishDate !== false && (
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {episode.created_at
                                ? new Date(episode.created_at).toLocaleDateString()
                                : '—'}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Eye className="w-3 h-3 mr-1" />
                            {episode.metrics_cache?.views || 0} views
                          </span>
                        </div>
                        
                        {isOwner ? (
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/series/${seriesId}/upload?episodeId=${episode.id}`}
                              className="text-xs text-zinc-300 hover:text-white underline"
                            >
                              Edit
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleTogglePublish(episode)}
                              disabled={updatingEpisodeId === episode.id}
                              className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 disabled:opacity-50"
                            >
                              {updatingEpisodeId === episode.id ? 'Saving...' :
                                episode.is_published === 'published' ? 'Unpublish' : 'Republish'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(episode)}
                              className="text-xs px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/60 text-red-400 hover:text-red-300 border border-red-800/50 transition-colors"
                              data-testid={`delete-episode-btn-${episode.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <ChevronRight className="w-6 h-6 text-zinc-600 group-hover:text-orange-500 transition-colors flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {episodes.length === 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No episodes available yet.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md" data-testid="delete-episode-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Episode?
            </DialogTitle>
            <DialogDescription className="text-zinc-400 pt-2">
              This will remove the episode from your series and readers will no longer be able to access it.
              {deleteTarget?.is_published === 'published' && (
                <span className="block mt-2 text-amber-400 font-medium">
                  This episode is currently published and visible to readers.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className="flex-1 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteEpisode}
              disabled={deleting}
              className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ============================================================================
// SERIES INFO PANEL SECTION
// ============================================================================

/**
 * SeriesInfoPanelSection - Description, tags, and stats
 */
export const SeriesInfoPanelSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  
  const { data, loading, error } = useSeriesData(seriesId);
  const { episodes } = useEpisodes(seriesId);
  
  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8 bg-zinc-900/50" data-testid="series-info-loading">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="py-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionError message="Failed to load series info" />
        </div>
      </section>
    );
  }

  const { series, stats } = data;

  return (
    <section className="py-8 bg-zinc-900/50" data-testid="series-info-section">
      <div className="max-w-4xl mx-auto px-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-4">About</h2>
            
            {/* Description */}
            {props.showDescription !== false && series.description && (
              <p className="text-zinc-300 leading-relaxed mb-6">{series.description}</p>
            )}

            {/* Tags */}
            {props.showTags !== false && series.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {series.genres.map((genre) => (
                  <Badge key={genre} variant="outline" className="border-zinc-600 text-zinc-300">
                    {genre}
                  </Badge>
                ))}
              </div>
            )}

            {/* Quick Stats */}
            {props.showStats !== false && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                  <Eye className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{stats?.views?.toLocaleString() || 0}</div>
                  <div className="text-sm text-zinc-400">Views</div>
                </div>
                <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                  <Users className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{stats?.subscribers?.toLocaleString() || 0}</div>
                  <div className="text-sm text-zinc-400">Subscribers</div>
                </div>
                <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                  <BookOpen className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{episodes.length || stats?.chapters || 0}</div>
                  <div className="text-sm text-zinc-400">Episodes</div>
                </div>
                <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                  <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{stats?.rating || 0}/10</div>
                  <div className="text-sm text-zinc-400">Rating</div>
                </div>
              </div>
            )}

            {/* Read Buttons */}
            {episodes.length > 0 && (
              <div className="flex flex-wrap gap-3">
                <Link to={`/series/${seriesId}/read/${episodes[0].id}`}>
                  <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Read First Episode
                  </Button>
                </Link>
                {episodes.length > 1 && (
                  <Link to={`/series/${seriesId}/read/${episodes[episodes.length - 1].id}`}>
                    <Button variant="outline" className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white">
                      <Zap className="w-4 h-4 mr-2" />
                      Read Latest
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// ============================================================================
// SERIES COMMENTS SECTION
// ============================================================================

/**
 * SeriesCommentsSection - Comments with reactions
 */
export const SeriesCommentsSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const props = block?.props || {};
  const limit = props.limit || 20;
  const heading = props.heading || 'Comments';
  
  const { comments, setComments, loading, error } = useComments(seriesId, limit);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      navigate('/auth');
      return;
    }
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }
    
    setPosting(true);
    try {
      const response = await apiClient.post(`/series/${seriesId}/comments`, {
        content: newComment,
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (err) {
      console.error('Error posting comment:', err);
      toast.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <section className="py-8" data-testid="comments-loading">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  return (
    <section className="py-8" data-testid="series-comments-section">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <MessageCircle className="w-6 h-6 mr-2 text-orange-400" />
          {heading} ({comments.length})
        </h2>

        {/* Comment Form */}
        {props.allowComments !== false && (
          user ? (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardContent className="p-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="bg-zinc-800 border-zinc-700 text-white mb-3"
                  rows={3}
                />
                <Button
                  onClick={handleAddComment}
                  disabled={posting}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Post Comment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardContent className="p-6 text-center">
                <p className="text-zinc-400 mb-3">Login to leave a comment</p>
                <Link to="/auth">
                  <Button className="bg-orange-500 hover:bg-orange-600">Login</Button>
                </Link>
              </CardContent>
            </Card>
          )
        )}

        {/* Comments List */}
        <div className="space-y-4">
          {comments.map((comment) => (
            <Card key={comment.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-orange-500 text-white">
                      {comment.username?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-white font-medium">{comment.username}</span>
                      <span className="text-zinc-500 text-sm">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-zinc-300 mb-3">{comment.content}</p>
                    {props.showReactions !== false && (
                      <div className="flex items-center space-x-4 text-sm">
                        <button className="flex items-center space-x-1 text-zinc-400 hover:text-orange-400">
                          <Flame className="w-4 h-4" />
                          <span>{comment.reactions?.fire || 0}</span>
                        </button>
                        <button className="flex items-center space-x-1 text-zinc-400 hover:text-red-400">
                          <Heart className="w-4 h-4" />
                          <span>{comment.reactions?.heart || 0}</span>
                        </button>
                        <button className="flex items-center space-x-1 text-zinc-400 hover:text-yellow-400">
                          <Laugh className="w-4 h-4" />
                          <span>{comment.reactions?.laugh || 0}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {comments.length === 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-12 text-center">
                <MessageCircle className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No comments yet. Be the first!</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// RELATED SERIES SECTION
// ============================================================================

/**
 * RelatedSeriesSection - Similar series recommendations
 */
export const RelatedSeriesSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  
  const props = block?.props || {};
  const maxItems = props.maxItems || 6;
  const heading = props.heading || 'You Might Also Like';
  
  const { related, loading, error } = useRelatedSeries(seriesId, maxItems);

  if (loading) {
    return (
      <section className="py-8" data-testid="related-series-loading">
        <div className="max-w-7xl mx-auto px-6">
          <div className="h-8 bg-zinc-800 rounded w-48 mb-6 animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-zinc-800 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || related.length === 0) {
    return null; // Don't show section if no related content
  }

  return (
    <section className="py-8" data-testid="related-series-section">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-2xl font-bold text-white mb-6">{heading}</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {related.map((item) => (
            <Link
              key={item.id}
              to={`/series/${item.id}`}
              className="group block"
            >
              <div className="aspect-[3/4] bg-zinc-800 rounded-lg overflow-hidden mb-2">
                {item.cover_url ? (
                  <img
                    src={item.cover_url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-zinc-600" />
                  </div>
                )}
              </div>
              <h3 className="text-white font-medium text-sm truncate group-hover:text-orange-400 transition-colors">
                {item.title}
              </h3>
              <p className="text-zinc-500 text-xs truncate">
                {item.creator_name}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SERIES CREATOR SPOTLIGHT SECTION
// ============================================================================

/**
 * SeriesCreatorSpotlightSection - Creator info card
 */
export const SeriesCreatorSpotlightSection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  
  const { data, loading, error } = useSeriesData(seriesId);
  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8 bg-zinc-900/50" data-testid="creator-spotlight-loading">
        <div className="max-w-md mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  if (error || !data?.creator) {
    return null;
  }

  const { creator } = data;
  const profile = creator.profile;

  return (
    <section className="py-8 bg-zinc-900/50" data-testid="series-creator-section">
      <div className="max-w-md mx-auto px-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-white mb-4">About the Creator</h3>
            
            <Link
              to={profile?.handle ? `/creator/${profile.handle}` : `/creator/${profile?.id}`}
              className="block mb-4"
            >
              <div className="flex items-center space-x-3 p-3 bg-black/50 rounded-lg hover:bg-black/70 transition-colors">
                {props.showAvatar !== false && (
                  <Avatar className="w-16 h-16 border-2 border-orange-500">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback className="bg-orange-500 text-white text-xl">
                      {profile?.display_name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="flex-1">
                  <p className="text-white font-bold">{profile?.display_name}</p>
                  <p className="text-zinc-400 text-sm">@{profile?.handle}</p>
                </div>
              </div>
            </Link>

            {props.showBio !== false && profile?.bio_richtext && (
              <p className="text-zinc-300 text-sm mb-4 line-clamp-3">
                {profile.bio_richtext}
              </p>
            )}

            <div className="space-y-2">
              <Link to={profile?.handle ? `/creator/${profile.handle}` : `/creator/${profile?.id}`}>
                <Button
                  variant="outline"
                  className="w-full border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                >
                  View Profile
                </Button>
              </Link>
              {props.showFollowButton !== false && (
                <Button
                  variant="outline"
                  className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Follow Creator
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// ============================================================================
// SERIES CTA SECTION
// ============================================================================

/**
 * SeriesCTASection - Support/tip call-to-action
 */
export const SeriesCTASection = ({ 
  block, 
  seriesId: propSeriesId,
  isPreview = false 
}) => {
  const { seriesId: paramSeriesId } = useParams();
  const seriesId = propSeriesId || paramSeriesId;
  
  const { data } = useSeriesData(seriesId);
  const props = block?.props || {};
  const heading = props.heading || 'Support This Series';

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: data?.series?.title,
        text: data?.series?.description,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <section 
      className="py-12 bg-gradient-to-r from-orange-500 to-red-500" 
      data-testid="series-cta-section"
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">{heading}</h2>
        <p className="text-white/80 mb-8 max-w-2xl mx-auto">
          Love this series? Show your support to help the creator continue making amazing content!
        </p>
        
        <div className="flex flex-wrap justify-center gap-4">
          {props.showTipButton !== false && (
            <Button
              className="bg-white text-orange-500 hover:bg-white/90 font-bold"
              size="lg"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Send a Tip
            </Button>
          )}
          
          {props.showShareButtons !== false && (
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              size="lg"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share Series
            </Button>
          )}
          
          {props.showPremiumUnlock !== false && (
            <Button
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              size="lg"
            >
              <Star className="w-5 h-5 mr-2" />
              Unlock Premium
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  SeriesHeroSection,
  EpisodeListSection,
  SeriesInfoPanelSection,
  SeriesCommentsSection,
  RelatedSeriesSection,
  SeriesCreatorSpotlightSection,
  SeriesCTASection,
  // Hooks for external use
  useSeriesData,
  useEpisodes,
  useComments,
  useRelatedSeries,
};
